import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HomeContent from '../seo/content/HomeContent';
import { dbHelpers } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import JoinTripModal from '../components/JoinTripModal';
import HomeTripScheduleSection from '../components/HomeTripScheduleSection';
import { isSharedTransfersEnabled } from '../config/features';
import { isSalePricingActive } from '../utils/salePricingOverlay';
import { resolveTransferSaleCampaign } from '../seo/transferSaleCampaign';

const RouterLink = ({ href, className, children }) => (
  <Link to={href} className={className}>
    {children}
  </Link>
);

const Home = () => {
  const sharedEnabled = isSharedTransfersEnabled();
  const sale = resolveTransferSaleCampaign({
    active: isSalePricingActive(),
    sharedEnabled,
  });
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(sharedEnabled);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinInitialRouteValue, setJoinInitialRouteValue] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (sharedEnabled) loadTrips();
  }, [sharedEnabled]);

  const isUserJoinedToTrip = (trip) => {
    if (!user) return false;
    return trip.participants?.some((p) => p.user_id === user.id) || false;
  };

  const isTripCreator = (trip) => user && trip?.creator_id === user.id;

  const loadTrips = async () => {
    try {
      const { data, error } = await dbHelpers.getOpenTripRequests();
      if (error) throw error;
      setTrips(data || []);
    } catch (error) {
      console.error('Error loading trips:', error);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTrip = (trip) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to join trips');
      return;
    }
    setSelectedTrip(trip);
    setJoinModalOpen(true);
  };

  const handleJoinSubmit = async (groupSize) => {
    if (!selectedTrip) return;
    if ((selectedTrip.seats_filled || 0) + groupSize > 6) {
      toast.error('Not enough seats available.');
      return;
    }
    setJoinModalOpen(false);
    loadTrips();
  };

  const handlePanelJoin = (trip, legValue) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to join trips');
      return;
    }
    setJoinInitialRouteValue(legValue ?? null);
    handleJoinTrip(trip);
  };

  const liveSlot = sharedEnabled ? (
    <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-20 border-b border-riverside-sand">
      <HomeTripScheduleSection
        homeTeaser
        trips={trips}
        loading={loading}
        selectedTrip={selectedTrip}
        onSelectTrip={setSelectedTrip}
        onCloseTrip={() => setSelectedTrip(null)}
        isUserJoined={isUserJoinedToTrip}
        isTripCreator={isTripCreator}
        isAuthenticated={isAuthenticated}
        userId={user?.id ?? null}
        onJoin={handlePanelJoin}
      />
    </section>
  ) : null;

  return (
    <>
      <HomeContent
        sale={sale}
        sharedEnabled={sharedEnabled}
        liveSlot={liveSlot}
        LinkComponent={RouterLink}
      />
      {sharedEnabled && (
        <JoinTripModal
          isOpen={joinModalOpen}
          onClose={() => {
            setJoinModalOpen(false);
            setJoinInitialRouteValue(null);
          }}
          onJoin={handleJoinSubmit}
          trip={selectedTrip}
          initialRouteValue={joinInitialRouteValue}
        />
      )}
    </>
  );
};

export default Home;
